// A/B report panel — fetches /api/analytics/ab-test?campaign_id=X and renders
// a side-by-side scoreboard for the two cadence variants. Also handles the
// "Promote winner" flow. Rendered inline in the Campaign edit form when
// A/B is enabled.
import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Trophy, TrendingUp, RefreshCw, Zap, AlertTriangle } from "lucide-react";

const STAGE_LABELS = {
  scraped: "Scraped", contacted: "Contacted", answered: "Answered",
  qualified: "Qualified", booked: "Booked", demo: "Demo", won: "Won", lost: "Lost",
};

const fmtRate = (r) => (r == null ? "—" : `${(r * 100).toFixed(1)}%`);
const fmtNum = (n) => (n == null ? "—" : String(n));

export default function ABReportPanel({ campaignId, onPromoted }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [promoting, setPromoting] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/analytics/ab-test?campaign_id=${campaignId}`);
      setReport(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load A/B report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (campaignId) load(); }, [campaignId]);  // eslint-disable-line

  const promote = async (variant) => {
    setPromoting(variant);
    try {
      await api.post(`/campaigns/${campaignId}/promote-variant`, { variant });
      toast.success(`Variant ${variant} promoted to primary cadence. A/B test disabled.`);
      onPromoted?.(variant);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Promote failed");
    } finally {
      setPromoting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-zinc-500 p-4" data-testid="ab-report-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading A/B report…
      </div>
    );
  }
  if (!report) return null;

  const { variants, winner, leader, lift, significance } = report;
  const totalAssigned = (variants.A.leads_assigned || 0) + (variants.B.leads_assigned || 0);

  return (
    <div className="border border-blue-200 bg-blue-50/30 p-4 mt-5" data-testid="ab-report-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#002FA7] flex items-center gap-1.5">
            <Trophy className="w-3 h-3" /> A/B Live Scoreboard
          </div>
          <p className="text-xs text-zinc-600 mt-0.5">
            {totalAssigned} leads assigned · Winner shown only when statistically significant (p&lt;0.05).
          </p>
        </div>
        <button
          type="button" onClick={load}
          data-testid="btn-refresh-ab-report"
          className="border border-zinc-300 hover:border-[#002FA7] text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 flex items-center gap-1"
        >
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {/* Winner / leader banner */}
      {winner ? (
        <div className="border border-emerald-300 bg-emerald-50 px-3 py-2 mb-4 flex items-start gap-2" data-testid="ab-winner-banner">
          <Trophy className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
          <div className="text-xs text-emerald-900">
            <strong>Winner: Variant {winner}</strong> · booking-rate lift <span className="font-mono">+{Math.round((lift || 0) * 100)}%</span> · z=<span className="font-mono">{significance.bookings.z}</span> · p=<span className="font-mono">{significance.bookings.p_value}</span>.
            Statistically significant — safe to promote.
          </div>
        </div>
      ) : leader ? (
        <div className="border border-amber-300 bg-amber-50 px-3 py-2 mb-4 flex items-start gap-2" data-testid="ab-leader-banner">
          <TrendingUp className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
          <div className="text-xs text-amber-900">
            <strong>Leader: Variant {leader}</strong> ({lift != null ? `+${Math.round(lift * 100)}%` : ""}) — not yet statistically significant.
            {significance.bookings.note && <span className="italic"> {significance.bookings.note}.</span>}
            Keep the test running until you have ≥10 leads per variant AND ≥3 bookings total.
          </div>
        </div>
      ) : (
        <div className="border border-zinc-200 bg-zinc-50 px-3 py-2 mb-4 flex items-start gap-2" data-testid="ab-nodata-banner">
          <AlertTriangle className="w-4 h-4 text-zinc-500 mt-0.5 shrink-0" />
          <div className="text-xs text-zinc-600">
            No signal yet — waiting for new leads to enter the campaign.
          </div>
        </div>
      )}

      {/* Side-by-side table */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {["A", "B"].map((v) => {
          const d = variants[v];
          const isWinner = winner === v;
          const isLeader = !winner && leader === v;
          return (
            <div
              key={v}
              className={`border p-3 bg-white ${
                isWinner ? "border-emerald-400" : isLeader ? "border-amber-400" : "border-zinc-200"
              }`}
              data-testid={`ab-variant-${v.toLowerCase()}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className={`text-[10px] font-bold uppercase tracking-wider ${
                    v === "A" ? "text-[#002FA7]" : "text-amber-700"
                  }`}>
                    Variant {v} {v === "A" ? "· control" : "· challenger"}
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    {d.leads_assigned} leads assigned
                  </div>
                </div>
                {(isWinner || isLeader) && (
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 ${
                    isWinner ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"
                  }`}>
                    {isWinner ? "Winner" : "Leader"}
                  </span>
                )}
              </div>

              {/* Primary "cita" columns: booked / demo / won */}
              <div className="grid grid-cols-3 gap-2 mb-3">
                {["booked", "demo", "won"].map((stage) => (
                  <div key={stage} className="border border-zinc-200 px-2 py-1.5 text-center bg-zinc-50" data-testid={`ab-${v.toLowerCase()}-${stage}`}>
                    <div className="text-[9px] font-bold uppercase tracking-wider text-zinc-500">
                      {STAGE_LABELS[stage]}
                    </div>
                    <div className="text-lg font-mono font-bold text-zinc-900">
                      {d.stages[stage]}
                    </div>
                    <div className="text-[10px] text-zinc-500 font-mono">
                      {fmtRate(d[`${stage}_rate`])}
                    </div>
                  </div>
                ))}
              </div>

              {/* Secondary metrics */}
              <div className="grid grid-cols-2 gap-1.5 text-[11px] font-mono">
                <div className="flex justify-between px-1 py-0.5 border-b border-zinc-100">
                  <span className="text-zinc-500">Contacted</span>
                  <span className="text-zinc-900">{fmtNum(d.leads_contacted)}</span>
                </div>
                <div className="flex justify-between px-1 py-0.5 border-b border-zinc-100">
                  <span className="text-zinc-500">Responded</span>
                  <span className="text-zinc-900">{fmtNum(d.leads_responded)}</span>
                </div>
                <div className="flex justify-between px-1 py-0.5 border-b border-zinc-100">
                  <span className="text-zinc-500">Outbound</span>
                  <span className="text-zinc-900">{fmtNum(d.outbound_sent)}</span>
                </div>
                <div className="flex justify-between px-1 py-0.5 border-b border-zinc-100">
                  <span className="text-zinc-500">Resp. rate</span>
                  <span className="text-zinc-900">{fmtRate(d.response_rate)}</span>
                </div>
              </div>

              {/* Promote button */}
              <button
                type="button"
                onClick={() => promote(v)}
                disabled={promoting === v || d.leads_assigned === 0}
                data-testid={`btn-promote-${v.toLowerCase()}`}
                className={`w-full mt-3 px-3 py-2 text-[10px] font-bold uppercase tracking-widest flex items-center justify-center gap-1.5 transition-colors ${
                  isWinner
                    ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                    : "border border-zinc-300 hover:border-zinc-500 text-zinc-700 hover:bg-zinc-50"
                } disabled:opacity-40 disabled:cursor-not-allowed`}
              >
                {promoting === v ? (
                  <><Loader2 className="w-3 h-3 animate-spin" /> Promoting…</>
                ) : (
                  <><Zap className="w-3 h-3" /> Promote Variant {v} → primary</>
                )}
              </button>
            </div>
          );
        })}
      </div>

      <p className="text-[10px] text-zinc-500 font-mono mt-3">
        <strong>Promote</strong> = copies the chosen variant into <em>sequence_steps</em>, clears variant B, and turns off A/B. Existing leads keep their step index; only new touches use the winner.
      </p>
    </div>
  );
}
